import { GEN_KINDS, GEN_KIND_LABEL, type GenKind } from '@ai-studio/shared';

import { DECIDER_COMBOS, DECIDER_LABEL, effectiveDecider } from '../../lib/generation.js';
import { selectWaiting, useGeneration } from '../../state/generation.js';
import { useTeam } from '../../state/team.js';
import { useWorkspace } from '../../state/workspace.js';
import { AutonomousButton } from '../Chat.js';
import { Toggle } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import './generation.css';

const ALLOW_HINT: Record<GenKind, string> = {
  model: 'The AI may generate 3D models on this PC. Off hides the tool from the AI.',
  pixel: 'The AI may generate pixel art and images on this PC. Off hides the tool from the AI.',
  speech: 'The AI may generate speech on this PC. Off hides the tool from the AI.',
  music: 'The AI may generate music on this PC. Off hides the tool from the AI.',
  sfx: 'The AI may generate sound effects on this PC. Off hides the tool from the AI.',
  video:
    'Video Studio may run local AI video models (keyframes, animatics, shots, lip sync, upscaling) on this PC. Off still allows engine-recorded shots and assembly.',
};

const ONLY_HINT: Record<GenKind, string> = {
  model: 'Every new 3D model is generated locally; the AI never hand-builds one.',
  pixel: 'All new pixel art and images are generated locally instead of drawn by hand.',
  speech: 'All speech is generated locally; no other voice source is used.',
  music: 'All music is generated locally; no hand-written tracks.',
  sfx: 'All sound effects are generated locally; no hand-made ones.',
  video: 'All video clips are generated locally.',
};

/** Badge with the number of generations waiting for the user. */
export function WaitingBadge() {
  const waiting = useGeneration(selectWaiting);
  if (waiting === 0) return null;
  return (
    <span className="gen-badge" aria-label={`${waiting} waiting on you`}>
      {waiting}
    </span>
  );
}

/**
 * Every local-generation setting, shown at the top of the assistant's
 * Generations tab: per type whether local models may be used at all and
 * whether they are always used, then who approves (auto-approve and
 * autonomous mode).
 */
export function LocalGenSettings() {
  const settings = useGeneration((s) => s.settings);
  const setType = useGeneration((s) => s.setType);
  const setAuto = useGeneration((s) => s.setAutoApprove);
  const members = useTeam((s) => s.members);
  const projectId = useWorkspace((s) => s.projectId);
  const assistant = members.find((m) => m.role === 'assistant');
  const autonomous = assistant?.autonomousWork === true;
  const who = effectiveDecider(settings, autonomous);

  return (
    <details className="gen-settings" open>
      <summary>
        <Icon name="local" size={13} /> Generation settings
        <span className="muted gen-settings-now">{DECIDER_LABEL[who]}</span>
      </summary>
      <p className="muted gen-line">Local generation runs on this PC. Only one job runs at a time across all projects.</p>
      <table className="gen-table">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col" title="The AI may use local models for this type at all">
              Use local models
            </th>
            <th scope="col" title="Every item of this type comes from a local model">
              Always local
            </th>
            <th scope="col" title="Requests and results of this type are approved without waiting for anyone">
              Auto-approve
            </th>
          </tr>
        </thead>
        <tbody>
          {GEN_KINDS.map((kind) => {
            const t = settings.types[kind];
            return (
              <tr key={kind}>
                <th scope="row">{GEN_KIND_LABEL[kind]}</th>
                <td title={ALLOW_HINT[kind]}>
                  <Toggle
                    checked={t.allow}
                    onChange={(allow) => void setType(kind, { allow })}
                    ariaLabel={`Use local models for ${GEN_KIND_LABEL[kind]}`}
                  />
                </td>
                <td title={t.allow ? ONLY_HINT[kind] : 'Turn on "Use local models" first'} className={t.allow ? undefined : 'is-disabled'}>
                  <Toggle
                    checked={t.allow && t.only}
                    disabled={!t.allow}
                    onChange={(only) => void setType(kind, { only })}
                    ariaLabel={`Always use local models for ${GEN_KIND_LABEL[kind]}`}
                  />
                </td>
                <td
                  title={settings.autoApprove ? 'Auto-approve is on for every type (below)' : `Approve ${GEN_KIND_LABEL[kind].toLowerCase()} requests and results automatically`}
                  className={settings.autoApprove ? 'is-disabled' : undefined}
                >
                  <Toggle
                    checked={settings.autoApprove || t.autoApprove === true}
                    disabled={settings.autoApprove}
                    onChange={(autoApprove) => void setType(kind, { autoApprove })}
                    ariaLabel={`Auto-approve ${GEN_KIND_LABEL[kind]}`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="gen-approvals">
        <strong>Approvals</strong>
        <div className="gen-type-row">
          <Toggle checked={settings.autoApprove} onChange={(on) => void setAuto(on)} label="Auto-approve everything" />
          <span className="muted">Every type: requests start and results are accepted automatically. Off: the per-type Auto-approve column applies.</span>
        </div>
        {assistant && projectId !== null && (
          <div className="gen-type-row">
            <AutonomousButton projectId={projectId} assistant={assistant} />
            <span className="muted">Autonomous mode: with auto-approve off, the assistant approves, rejects, redoes and refines instead of you.</span>
          </div>
        )}
        <ul className="gen-combos" aria-label="Who decides">
          {DECIDER_COMBOS.map((c) => {
            const on = c.autonomous === autonomous && c.autoApprove === settings.autoApprove;
            return (
              <li key={c.title} className={on ? 'is-on' : undefined}>
                <b>{c.title}:</b> {c.text}
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}

/** Header button: opens the Generations tab (settings and approvals). */
export function LocalGenButton({ onOpen, active }: { onOpen: () => void; active: boolean }) {
  return (
    <button
      type="button"
      className={`ibtn gen-btn${active ? ' is-active' : ''}`}
      title="Generations: local generation settings, approvals and the queue"
      aria-label="Open generations"
      onClick={onOpen}
    >
      <Icon name="local" size={15} />
      <WaitingBadge />
    </button>
  );
}
